/**
 * @file ReconciliationEngine.ts
 * @description The heart of the Unified Hospital Operations Reporting system.
 *
 * This module merges data from three intentionally conflicting sources:
 *   1. HIS  — Hospital Information System (clinical authority)
 *   2. Lab  — Lab Order-to-Result turnaround log
 *   3. Bed  — Manually maintained Bed Occupancy Sheet (physical authority)
 *
 * It applies FOUR explicit reconciliation rules (defined in the spec),
 * never silently drops conflicting rows, and returns:
 *   - `unifiedRecords`  — the merged, reconciled patient/bed objects
 *   - `auditLog`        — every conflict detected with full provenance
 *   - `metrics`         — aggregated operational statistics
 *
 * ============================================================================
 * RECONCILIATION RULES (implemented exactly as specified)
 * ============================================================================
 *
 * Rule 1 — Status vs. Physical (Status Mismatch):
 *   If HIS.clinical_status = "Discharged" AND BedSheet.physical_status = "Occupied"
 *   → Resolved bed status = "Bed Requires Cleaning/Check"
 *   Rationale: Trust HIS for clinical ground truth (patient is gone),
 *              trust Bed Sheet for physical reality (bed not yet turned over).
 *
 * Rule 2 — Location Mismatch (Location Desync):
 *   If HIS.assigned_bed ≠ BedSheet.bed_id for the same patient_id
 *   → Accept BedSheet.bed_id as resolved_bed (physical truth wins)
 *   → Log a "Location Desync" warning in the audit log
 *
 * Rule 3 — Missing HIS Profile (Orphaned Lab Record):
 *   If a Lab row's patient_id has NO matching row in HIS
 *   → Create a temporary UnifiedRecord with is_orphaned_lab_record = true
 *   → patient_id prefix: "ORPHAN-<order_id>"
 *   → Do NOT discard; surface it in the UI for manual review
 *
 * Rule 4 — Timestamp Anomaly (Post-Discharge Anomaly):
 *   If Lab.result_datetime > HIS.discharge_datetime for the same patient
 *   → Flag the individual lab result as post_discharge_anomaly = true
 *   → Log a "Post-Discharge Anomaly" in the audit log
 * ============================================================================
 *
 * @author Unified Hospital Ops Team
 * @version 1.0.0
 */

import {
  RawHIS,
  RawLab,
  RawBed,
  UnifiedRecord,
  AuditLogEntry,
  SystemMetrics,
  ConflictType,
  ResolvedBedStatus,
  UnifiedLabResult,
} from "../types";
import costCatalogueData from "../data/cost_catalogue.json";

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Parses a date string (ISO-8601 or "YYYY-MM-DD HH:MM:SS") into a Date object.
 * Returns null if the string is falsy or unparseable.
 */
function parseDate(dateStr: string | undefined | null): Date | null {
  if (!dateStr || dateStr.trim() === "") return null;
  // Normalize "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM:SS" for universal parsing
  const normalized = dateStr.trim().replace(" ", "T");
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Generates a unique reconciliation run ID based on current timestamp.
 */
function generateRunId(): string {
  return `RUN-${new Date().toISOString().replace(/[:.]/g, "-")}`;
}

/**
 * Computes the nth-percentile value from a sorted numeric array.
 * Used for P90 Lab TAT calculation.
 */
function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, Math.min(idx, sorted.length - 1))];
}

// =============================================================================
// MAIN ENGINE EXPORT
// =============================================================================

/**
 * The result returned by runReconciliation().
 */
export interface ReconciliationResult {
  unifiedRecords: UnifiedRecord[];
  auditLog: AuditLogEntry[];
  metrics: SystemMetrics;
  reconciliation_run_id: string;
}

/**
 * @function runReconciliation
 * @description Entry point for the reconciliation engine.
 *              Accepts the three raw data arrays, applies all four rules,
 *              and returns the fully merged result.
 *
 * @param his   Array of raw HIS rows (from CSV)
 * @param lab   Array of raw Lab rows (from CSV)
 * @param bed   Array of raw Bed Sheet rows (from CSV)
 * @returns     ReconciliationResult with unified records, audit log, and metrics
 */
export function runReconciliation(
  his: RawHIS[],
  lab: RawLab[],
  bed: RawBed[]
): ReconciliationResult {
  const runId = generateRunId();
  const runTimestamp = new Date().toISOString();

  // Mutable collections we'll build up
  const unifiedRecords: UnifiedRecord[] = [];
  const auditLog: AuditLogEntry[] = [];

  // -------------------------------------------------------------------------
  // BUILD LOOKUP MAPS
  // Normalise all patient_id keys to uppercase trimmed strings so that case
  // or whitespace differences between sources don't cause false mismatches.
  // -------------------------------------------------------------------------

  /** Map: patient_id → RawHIS row */
  const hisMap = new Map<string, RawHIS>();
  for (const row of his) {
    const key = row.patient_id.trim().toUpperCase();
    hisMap.set(key, row);
  }

  /**
   * Map: patient_id → RawBed row
   * Note: A bed sheet can have multiple beds, but each patient_id should
   * appear at most once. If duplicates exist, last-write wins (logged).
   */
  const bedByPatient = new Map<string, RawBed>();
  /** Map: bed_id → RawBed row (for beds that may have no patient) */
  const bedById = new Map<string, RawBed>();
  for (const row of bed) {
    bedById.set(row.bed_id.trim().toUpperCase(), row);
    if (row.patient_id && row.patient_id.trim() !== "") {
      bedByPatient.set(row.patient_id.trim().toUpperCase(), row);
    }
  }

  /**
   * Map: patient_id → RawLab[] (grouped; one patient can have many orders)
   */
  const labByPatient = new Map<string, RawLab[]>();
  for (const row of lab) {
    const key = row.patient_id.trim().toUpperCase();
    if (!labByPatient.has(key)) {
      labByPatient.set(key, []);
    }
    labByPatient.get(key)!.push(row);
  }

  // -------------------------------------------------------------------------
  // Track which lab patient IDs have been matched to an HIS record.
  // Any remaining unmatched ones will become Orphaned Lab Records (Rule 3).
  // -------------------------------------------------------------------------
  const matchedLabPatientIds = new Set<string>();

  // =========================================================================
  // PASS 1: Iterate over HIS records as the primary axis of truth.
  //         For each HIS patient, merge in any corresponding Bed and Lab data.
  // =========================================================================
  for (const hisRow of his) {
    const patientKey = hisRow.patient_id.trim().toUpperCase();
    const correspondingBed = bedByPatient.get(patientKey) || null;
    const correspondingLab = labByPatient.get(patientKey) || [];

    // Mark lab patient as matched
    if (correspondingLab.length > 0) {
      matchedLabPatientIds.add(patientKey);
    }

    // Track which conflicts apply to this specific record
    const conflictFlags: ConflictType[] = [];
    let resolvedBed = hisRow.assigned_bed.trim();
    let resolvedBedStatus: ResolvedBedStatus = "Unknown";

    // -----------------------------------------------------------------------
    // Determine initial bed status from Bed Sheet (physical authority)
    // If no Bed Sheet row exists for this patient, fall back to HIS status.
    // -----------------------------------------------------------------------
    if (correspondingBed) {
      // Prefer physical bed ID from Bed Sheet (will be adjusted by Rule 2)
      resolvedBed = correspondingBed.bed_id.trim();

      switch (correspondingBed.physical_status.trim()) {
        case "Occupied":
          resolvedBedStatus = "Occupied";
          break;
        case "Available":
          resolvedBedStatus = "Available";
          break;
        case "Pending Clean":
          resolvedBedStatus = "Pending Clean";
          break;
        case "Maintenance":
          resolvedBedStatus = "Maintenance";
          break;
        default:
          resolvedBedStatus = "Unknown";
      }
    } else {
      // No Bed Sheet entry — infer from HIS clinical status
      if (hisRow.clinical_status.trim() === "Discharged") {
        resolvedBedStatus = "Available";
      } else if (hisRow.clinical_status.trim() === "Admitted") {
        resolvedBedStatus = "Occupied";
      } else {
        resolvedBedStatus = "Unknown";
      }
    }

    // =========================================================================
    // RULE 1 — Status vs. Physical (Status Mismatch)
    // =========================================================================
    // Condition: HIS = "Discharged" AND Bed Sheet = "Occupied"
    // This means the HIS has processed the discharge but the physical bed
    // has not yet been turned over (cleaned, inspected, made available).
    // Resolution: Override bed status to "Bed Requires Cleaning/Check".
    //             Retain the HIS clinical status as-is (patient is discharged).
    // =========================================================================
    if (
      hisRow.clinical_status.trim() === "Discharged" &&
      correspondingBed?.physical_status.trim() === "Occupied"
    ) {
      // Apply Rule 1 resolution
      resolvedBedStatus = "Bed Requires Cleaning/Check";
      conflictFlags.push("Status Mismatch");

      auditLog.push({
        timestamp: runTimestamp,
        patient_id: hisRow.patient_id,
        patient_name: hisRow.patient_name,
        conflict_type: "Status Mismatch",
        conflict_detail:
          `HIS clinical status is 'Discharged' but Bed Sheet records ` +
          `bed '${correspondingBed.bed_id}' as 'Occupied'. ` +
          `Patient may have left but bed has not yet been cleared.`,
        sources_involved: ["HIS", "BedSheet"],
        resolution_applied:
          "Resolved bed status to 'Bed Requires Cleaning/Check'. " +
          "HIS clinical status 'Discharged' retained as authoritative. " +
          "Bed Sheet 'Occupied' retained for physical tracking purposes.",
        reconciliation_run_id: runId,
      });
    }

    // =========================================================================
    // RULE 2 — Location Mismatch (Location Desync)
    // =========================================================================
    // Condition: HIS.assigned_bed ≠ BedSheet.bed_id for the same patient_id
    // Resolution: Trust the Bed Sheet for the physical bed assignment
    //             (a nurse physically recorded the location). Update the
    //             resolved_bed field to the Bed Sheet's bed_id.
    //             Log a "Location Desync" warning so ops can investigate.
    // =========================================================================
    const hisBed = hisRow.assigned_bed.trim().toUpperCase();
    const bedSheetBed = correspondingBed?.bed_id.trim().toUpperCase();

    if (
      correspondingBed &&
      bedSheetBed &&
      hisBed !== bedSheetBed
    ) {
      // Bed Sheet wins for physical location
      resolvedBed = correspondingBed.bed_id.trim();
      conflictFlags.push("Location Desync");

      auditLog.push({
        timestamp: runTimestamp,
        patient_id: hisRow.patient_id,
        patient_name: hisRow.patient_name,
        conflict_type: "Location Desync",
        conflict_detail:
          `HIS records patient in bed '${hisRow.assigned_bed}' ` +
          `but Bed Sheet records the same patient in bed '${correspondingBed.bed_id}'. ` +
          `This may indicate a manual bed transfer not yet reflected in HIS.`,
        sources_involved: ["HIS", "BedSheet"],
        resolution_applied:
          `Resolved bed assignment to '${correspondingBed.bed_id}' (Bed Sheet authority for physical location). ` +
          `HIS bed '${hisRow.assigned_bed}' preserved in raw data but not used as canonical location. ` +
          `Investigate whether a bed transfer order exists in HIS.`,
        reconciliation_run_id: runId,
      });
    }

    // =========================================================================
    // RULE 4 — Timestamp Anomaly (Post-Discharge Anomaly)
    // =========================================================================
    // Condition: Lab.result_datetime > HIS.discharge_datetime
    //            for the same patient_id
    // This can indicate: lab was ordered pre-discharge but resulted after;
    // or the discharge was backdated in HIS; or a data entry error.
    // Resolution: Flag the specific lab result. Do not discard it.
    //             Surface it for manual clinical review.
    // =========================================================================
    const dischargeDate = parseDate(hisRow.discharge_datetime);
    const unifiedLabResults: UnifiedLabResult[] = [];
    let labTatSum = 0;
    let labTatCount = 0;

    for (const labRow of correspondingLab) {
      const resultDate = parseDate(labRow.result_datetime);
      const tatMinutes =
        typeof labRow.tat_minutes === "string"
          ? parseFloat(labRow.tat_minutes) || 0
          : labRow.tat_minutes;

      let isPostDischargeAnomaly = false;

      if (
        dischargeDate !== null &&     // Patient has been discharged
        resultDate !== null &&         // Lab result has been finalized
        resultDate > dischargeDate     // Result came after discharge
      ) {
        isPostDischargeAnomaly = true;
        conflictFlags.push("Post-Discharge Anomaly");

        auditLog.push({
          timestamp: runTimestamp,
          patient_id: hisRow.patient_id,
          patient_name: hisRow.patient_name,
          conflict_type: "Post-Discharge Anomaly",
          conflict_detail:
            `Lab order '${labRow.order_id}' (${labRow.test_name}) was resulted on ` +
            `'${labRow.result_datetime}', which is AFTER the HIS discharge timestamp ` +
            `of '${hisRow.discharge_datetime}'. This order may have been drawn pre-discharge ` +
            `but processed after the patient left.`,
          sources_involved: ["HIS", "Lab"],
          resolution_applied:
            "Lab result retained in unified record. " +
            "Flagged as 'Post-Discharge Anomaly' for clinical and lab review. " +
            "TAT calculation includes this result.",
          reconciliation_run_id: runId,
        });
      }

      unifiedLabResults.push({
        order_id: labRow.order_id,
        test_name: labRow.test_name,
        order_datetime: labRow.order_datetime,
        result_datetime: labRow.result_datetime,
        tat_minutes: tatMinutes,
        result_status: labRow.result_status,
        priority: labRow.priority,
        post_discharge_anomaly: isPostDischargeAnomaly,
      });

      if (labRow.result_status.trim() === "Completed" && tatMinutes > 0) {
        labTatSum += tatMinutes;
        labTatCount++;
      }
    }

    const avgLabTat = labTatCount > 0 ? Math.round(labTatSum / labTatCount) : 0;

    // If no conflicts were found, mark as "Clean"
    if (conflictFlags.length === 0) {
      conflictFlags.push("Clean");
    }

    // =========================================================================
    // BILLING CALCULATION
    // =========================================================================
    let doctor_visits = 0;
    if (hisRow.revisit && !isNaN(Number(hisRow.revisit))) {
      doctor_visits = Number(hisRow.revisit);
    } else {
      const admDate = parseDate(hisRow.admission_datetime) || new Date();
      const discDate = parseDate(hisRow.discharge_datetime) || new Date();
      const diffTime = Math.abs(discDate.getTime() - admDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
      doctor_visits = diffDays;
    }
    const doctor_charge = doctor_visits * 500;

    let room_charge = 0;
    const admDateRoom = parseDate(hisRow.admission_datetime) || new Date();
    const discDateRoom = parseDate(hisRow.discharge_datetime) || new Date();
    const diffTimeRoom = Math.abs(discDateRoom.getTime() - admDateRoom.getTime());
    const diffDaysRoom = Math.ceil(diffTimeRoom / (1000 * 60 * 60 * 24)) || 1;
    const roomRate = 2000;
    room_charge = diffDaysRoom * roomRate;

    let surgery_charge = 0;
    if (hisRow.surgery && hisRow.surgery.trim().toLowerCase() !== "no" && hisRow.surgery.trim() !== "") {
      const surgeryName = hisRow.surgery.trim().toLowerCase();
      const foundSurgery = costCatalogueData.find(item => item.item.toLowerCase().includes(surgeryName) && item.category === "Surgery");
      if (foundSurgery) {
        surgery_charge = (foundSurgery.estimated_min + foundSurgery.estimated_max) / 2;
      } else {
        surgery_charge = 50000;
      }
    }

    let lab_charge = 0;
    for (const lab of correspondingLab) {
      const foundLab = costCatalogueData.find(item => item.item.toLowerCase() === lab.test_name.toLowerCase());
      if (foundLab) {
        lab_charge += (foundLab.estimated_min + foundLab.estimated_max) / 2;
      } else {
        lab_charge += 500;
      }
    }

    const total_charge = doctor_charge + room_charge + surgery_charge + lab_charge;

    // -----------------------------------------------------------------------
    // Assemble the Unified Record for this HIS patient
    // -----------------------------------------------------------------------
    const unifiedRecord: UnifiedRecord = {
      patient_id: hisRow.patient_id,
      patient_name: hisRow.patient_name,

      resolved_bed: resolvedBed,
      ward: hisRow.ward || correspondingBed?.ward || "Unknown",
      resolved_bed_status: resolvedBedStatus,

      his_clinical_status: hisRow.clinical_status,
      admission_datetime: hisRow.admission_datetime,
      discharge_datetime: hisRow.discharge_datetime,
      diagnosis_code: hisRow.diagnosis_code,
      surgery: hisRow.surgery || "",
      revisit: hisRow.revisit || "",
      labs_uses: hisRow.labs_uses || "",

      lab_results: unifiedLabResults,
      avg_lab_tat_minutes: avgLabTat,

      conflict_flags: conflictFlags,
      has_conflict: !conflictFlags.includes("Clean"),
      is_orphaned_lab_record: false,

      last_reconciled_at: runTimestamp,
      sources_present: [
        "HIS",
        ...(correspondingLab.length > 0 ? (["Lab"] as const) : []),
        ...(correspondingBed ? (["BedSheet"] as const) : []),
      ],
      doctor_visits,
      doctor_charge,
      lab_charge,
      room_charge,
      surgery_charge,
      total_charge,
    };

    unifiedRecords.push(unifiedRecord);
  }

  // =========================================================================
  // PASS 2 — RULE 3: Orphaned Lab Records
  // =========================================================================
  // After processing all HIS records, find any Lab patient IDs that had
  // NO matching HIS entry. These are Orphaned Lab Records.
  //
  // Condition: Lab.patient_id NOT found in HIS
  // Resolution: Create a temporary UnifiedRecord with:
  //             - is_orphaned_lab_record = true
  //             - patient_id prefixed with "ORPHAN-"
  //             - All HIS fields left as empty strings
  //             - conflict_flags = ["Orphaned Lab Record"]
  //
  // CRITICAL: These records are NOT discarded. They surface in both the
  //           unified view AND the audit log.
  // =========================================================================
  for (const [patientKey, labRows] of labByPatient.entries()) {
    // Skip if this patient was already matched to an HIS record
    if (matchedLabPatientIds.has(patientKey)) continue;

    // Group by patient_id — all labRows here share the same patient_id
    const representative = labRows[0];
    const orphanPatientId = `ORPHAN-${representative.patient_id}`;

    // Build lab results for orphan (no discharge date to check against)
    let labTatSum = 0;
    let labTatCount = 0;
    const unifiedLabResults: UnifiedLabResult[] = labRows.map((labRow) => {
      const tatMinutes =
        typeof labRow.tat_minutes === "string"
          ? parseFloat(labRow.tat_minutes) || 0
          : labRow.tat_minutes;

      if (labRow.result_status.trim() === "Completed" && tatMinutes > 0) {
        labTatSum += tatMinutes;
        labTatCount++;
      }

      return {
        order_id: labRow.order_id,
        test_name: labRow.test_name,
        order_datetime: labRow.order_datetime,
        result_datetime: labRow.result_datetime,
        tat_minutes: tatMinutes,
        result_status: labRow.result_status,
        priority: labRow.priority,
        post_discharge_anomaly: false, // Cannot check: no HIS record
      };
    });

    const avgLabTat = labTatCount > 0 ? Math.round(labTatSum / labTatCount) : 0;

    let lab_charge = 0;
    for (const lab of labRows) {
      const foundLab = costCatalogueData.find(item => item.item.toLowerCase() === lab.test_name.toLowerCase());
      if (foundLab) {
        lab_charge += (foundLab.estimated_min + foundLab.estimated_max) / 2;
      } else {
        lab_charge += 500;
      }
    }
    const doctor_visits = 0;
    const doctor_charge = 0;
    const room_charge = 0;
    const surgery_charge = 0;
    const total_charge = lab_charge;

    auditLog.push({
      timestamp: runTimestamp,
      patient_id: orphanPatientId,
      patient_name: `Unknown (Lab Patient ${representative.patient_id})`,
      conflict_type: "Orphaned Lab Record",
      conflict_detail:
        `Lab data contains ${labRows.length} order(s) for patient_id '${representative.patient_id}', ` +
        `but this patient_id does not exist in the HIS export. ` +
        `Orders involved: ${labRows.map((r) => r.order_id).join(", ")}.`,
      sources_involved: ["Lab"],
      resolution_applied:
        "Created a temporary unified profile marked as 'Orphaned Lab Record'. " +
        "All lab results are preserved. Clinical status, bed, and ward fields are blank. " +
        "Action Required: Verify the patient_id in the Lab system and reconcile with HIS manually.",
      reconciliation_run_id: runId,
    });

    unifiedRecords.push({
      patient_id: orphanPatientId,
      patient_name: `Unknown (Lab Patient ${representative.patient_id})`,

      resolved_bed: "",
      ward: "",
      resolved_bed_status: "Orphaned",

      his_clinical_status: "",
      admission_datetime: "",
      discharge_datetime: "",
      diagnosis_code: "",
      surgery: "",
      revisit: "",
      labs_uses: "",

      lab_results: unifiedLabResults,
      avg_lab_tat_minutes: avgLabTat,

      conflict_flags: ["Orphaned Lab Record"],
      has_conflict: true,
      is_orphaned_lab_record: true,

      last_reconciled_at: runTimestamp,
      sources_present: ["Lab"],
      doctor_visits,
      doctor_charge,
      lab_charge,
      room_charge,
      surgery_charge,
      total_charge,
    });
  }

  // =========================================================================
  // PASS 3 — Process beds with no patient (Available/Maintenance beds)
  // =========================================================================
  // Bed Sheet may list beds that have no patient. These are not in HIS.
  // We include them in the unified view for bed map completeness.
  // =========================================================================
  for (const bedRow of bed) {
    const patientKey = bedRow.patient_id?.trim().toUpperCase() || "";
    // Only process beds that are unoccupied (no patient_id) 
    // or whose patient_id isn't in HIS
    if (patientKey === "" || !hisMap.has(patientKey)) {
      // Don't double-add if we already processed via a matched HIS record
      const alreadyAdded = unifiedRecords.some(
        (r) =>
          r.resolved_bed.toUpperCase() === bedRow.bed_id.trim().toUpperCase() &&
          !r.is_orphaned_lab_record
      );
      if (alreadyAdded) continue;

      let resolvedBedStatus: ResolvedBedStatus;
      switch (bedRow.physical_status.trim()) {
        case "Available":
          resolvedBedStatus = "Available";
          break;
        case "Pending Clean":
          resolvedBedStatus = "Pending Clean";
          break;
        case "Maintenance":
          resolvedBedStatus = "Maintenance";
          break;
        default:
          resolvedBedStatus = "Unknown";
      }

      unifiedRecords.push({
        patient_id: `BED-ONLY-${bedRow.bed_id}`,
        patient_name: "",

        resolved_bed: bedRow.bed_id,
        ward: bedRow.ward,
        resolved_bed_status: resolvedBedStatus,

        his_clinical_status: "",
        admission_datetime: "",
        discharge_datetime: "",
        diagnosis_code: "",
        surgery: "",
        revisit: "",
        labs_uses: "",

        lab_results: [],
        avg_lab_tat_minutes: 0,

        conflict_flags: ["Clean"],
        has_conflict: false,
        is_orphaned_lab_record: false,

        last_reconciled_at: runTimestamp,
        sources_present: ["BedSheet"],
        doctor_visits: 0,
        doctor_charge: 0,
        lab_charge: 0,
        room_charge: 0,
        surgery_charge: 0,
        total_charge: 0,
      });
    }
  }

  // =========================================================================
  // COMPUTE SYSTEM METRICS
  // =========================================================================
  const metrics = computeMetrics(
    unifiedRecords,
    auditLog,
    his,
    lab,
    bed,
    runId,
    runTimestamp
  );

  return { unifiedRecords, auditLog, metrics, reconciliation_run_id: runId };
}

// =============================================================================
// METRICS COMPUTATION
// =============================================================================

/**
 * Computes aggregated SystemMetrics from the fully reconciled data.
 * Called once at the end of runReconciliation().
 */
function computeMetrics(
  records: UnifiedRecord[],
  auditLog: AuditLogEntry[],
  rawHis: RawHIS[],
  rawLab: RawLab[],
  rawBed: RawBed[],
  runId: string,
  timestamp: string
): SystemMetrics {
  // Filter out the "BED-ONLY" virtual records for patient-centric metrics
  const patientRecords = records.filter((r) => !r.patient_id.startsWith("BED-ONLY-"));

  // Bed counts — use all records for bed map completeness
  const totalBeds = rawBed.length;
  const occupiedBeds = records.filter(
    (r) => r.resolved_bed_status === "Occupied"
  ).length;
  const availableBeds = records.filter(
    (r) => r.resolved_bed_status === "Available"
  ).length;
  const pendingClean = records.filter(
    (r) =>
      r.resolved_bed_status === "Pending Clean" ||
      r.resolved_bed_status === "Bed Requires Cleaning/Check"
  ).length;
  const flaggedBeds = records.filter((r) => r.has_conflict).length;
  const occupancyRate =
    totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;

  // Lab metrics
  const allLabResults = patientRecords.flatMap((r) => r.lab_results);
  const completedLab = allLabResults.filter(
    (l) => l.result_status.trim() === "Completed"
  );
  const pendingLab = allLabResults.filter(
    (l) => l.result_status.trim() === "Pending"
  );
  const statOrders = allLabResults.filter(
    (l) => l.priority.trim() === "STAT"
  ).length;

  const tatValues = completedLab.map((l) => l.tat_minutes).filter((t) => t > 0);
  const sortedTat = [...tatValues].sort((a, b) => a - b);
  const avgTat =
    sortedTat.length > 0
      ? Math.round(sortedTat.reduce((a, b) => a + b, 0) / sortedTat.length)
      : 0;
  const p90Tat = percentile(sortedTat, 90);

  // Conflict counts
  const totalConflicts = auditLog.length;
  const statusMismatches = auditLog.filter(
    (e) => e.conflict_type === "Status Mismatch"
  ).length;
  const locationDesyncs = auditLog.filter(
    (e) => e.conflict_type === "Location Desync"
  ).length;
  const orphanedLabs = auditLog.filter(
    (e) => e.conflict_type === "Orphaned Lab Record"
  ).length;
  const postDischargeAnomalies = auditLog.filter(
    (e) => e.conflict_type === "Post-Discharge Anomaly"
  ).length;

  return {
    computed_at: timestamp,
    reconciliation_run_id: runId,

    total_beds: totalBeds,
    occupied_beds: occupiedBeds,
    available_beds: availableBeds,
    pending_clean_beds: pendingClean,
    flagged_beds: flaggedBeds,
    occupancy_rate_pct: occupancyRate,

    total_lab_orders: allLabResults.length,
    completed_lab_orders: completedLab.length,
    pending_lab_orders: pendingLab.length,
    avg_lab_tat_minutes: avgTat,
    p90_lab_tat_minutes: p90Tat,
    stat_orders: statOrders,

    total_conflicts: totalConflicts,
    status_mismatches: statusMismatches,
    location_desyncs: locationDesyncs,
    orphaned_lab_records: orphanedLabs,
    post_discharge_anomalies: postDischargeAnomalies,

    his_records_ingested: rawHis.length,
    lab_records_ingested: rawLab.length,
    bed_records_ingested: rawBed.length,
  };
}
